import React, { useEffect, useState } from 'react';

/**
 * Full-screen overlay animation for scoring events.
 * animation: { type: 'point'|'fault'|'win', teamId, playerName, shotType, match }
 */
export default function ScoreAnimation({ animation, match }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setVisible(true);
    return () => setVisible(false);
  }, [animation]);

  if (!visible || !animation) return null;

  const isTeam1 = animation.teamId === match?.team1_id;
  const teamName = isTeam1 ? match?.team1_name : match?.team2_name;
  const teamColor = isTeam1 ? '#27ae60' : '#2980b9';

  if (animation.type === 'win') {
    return (
      <div style={{ ...overlay, background: 'rgba(0,0,0,0.85)', zIndex: 9990 }}>
        <div style={{ textAlign: 'center', animation: 'slideUp 0.4s ease' }}>
          <div style={{ fontSize: '80px', animation: 'bounce 0.6s ease infinite' }}>🏆</div>
          <div style={{ fontSize: '48px', fontWeight: '900', color: '#FFD700', marginTop: '16px', textShadow: '0 0 40px rgba(255,215,0,0.8)' }}>
            WINNER!
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: teamColor, marginTop: '8px' }}>
            {teamName}
          </div>
          <div style={{ fontSize: '48px', fontWeight: '900', color: '#fff', marginTop: '16px' }}>
            {match?.team1_score} — {match?.team2_score}
          </div>
          {animation.playerName && (
            <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>
              Last point by {animation.playerName}
              {animation.shotType && ` · ${animation.shotType}`}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (animation.type === 'fault') {
    return (
      <div style={{ ...overlay, background: 'rgba(231,76,60,0.18)', backdropFilter: 'blur(4px)', zIndex: 9990, pointerEvents: 'none' }}>
        <div style={{ textAlign: 'center', animation: 'shake 0.5s ease' }}>
          <div style={{ fontSize: '56px' }}>⚡</div>
          <div style={{ fontSize: '36px', fontWeight: '900', color: '#e74c3c', marginTop: '8px', letterSpacing: '2px' }}>
            FAULT!
          </div>
          {animation.playerName && (
            <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>
              {animation.playerName}
            </div>
          )}
          <div style={{ fontSize: '18px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
            Point to {isTeam1 ? match?.team2_name : match?.team1_name}
          </div>
        </div>
      </div>
    );
  }

  // Regular point
  const isAce = animation.shotType === 'Serve';
  const isSmash = animation.shotType === 'Smash';
  const isSpecial = isAce || isSmash;

  return (
    <div style={{ ...overlay, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(2px)', zIndex: 9990, pointerEvents: 'none' }}>
      {/* Ball animation */}
      <div style={{ position: 'relative', textAlign: 'center' }}>
        {/* Ripple effect */}
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: '200px', height: '200px', borderRadius: '50%',
          border: `3px solid ${teamColor}`,
          transform: 'translate(-50%, -50%)',
          animation: 'ripple 0.6s ease-out',
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: '140px', height: '140px', borderRadius: '50%',
          border: `3px solid ${teamColor}`,
          transform: 'translate(-50%, -50%)',
          animation: 'ripple 0.6s 0.1s ease-out',
        }} />

        {/* Ball */}
        <div style={{
          fontSize: '52px',
          animation: isSmash ? 'bounce 0.4s ease' : 'slideUp 0.3s ease',
          filter: `drop-shadow(0 0 20px ${teamColor})`,
        }}>
          {isSmash ? '💥' : '🏓'}
        </div>

        {/* Point text */}
        <div style={{
          fontSize: isSpecial ? '44px' : '36px',
          fontWeight: '900',
          color: teamColor,
          textShadow: `0 0 30px ${teamColor}80`,
          letterSpacing: isSpecial ? '3px' : '1px',
          marginTop: '8px',
          animation: 'slideUp 0.25s ease',
        }}>
          {isAce ? '🎯 ACE!' : isSmash ? 'SMASH!' : 'POINT!'}
        </div>

        {/* Team name */}
        <div style={{
          fontSize: '20px', fontWeight: '700', color: '#fff',
          marginTop: '6px', animation: 'slideUp 0.3s 0.05s ease both',
        }}>
          {teamName}
        </div>

        {/* Player & shot */}
        {(animation.playerName || animation.shotType) && (
          <div style={{
            fontSize: '14px', color: 'rgba(255,255,255,0.6)',
            marginTop: '4px', animation: 'slideUp 0.3s 0.1s ease both',
          }}>
            {animation.playerName && <span>{animation.playerName}</span>}
            {animation.playerName && animation.shotType && ' · '}
            {animation.shotType && <span>{animation.shotType}</span>}
          </div>
        )}

        {/* New score */}
        <div style={{
          fontSize: '32px', fontWeight: '900',
          color: 'rgba(255,255,255,0.9)',
          marginTop: '12px',
          animation: 'scoreFlip 0.4s 0.1s ease both',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {match?.team1_score} — {match?.team2_score}
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
